/**
 * Rechtekatalog des Rollensystems (docs/rollensystem-plan.md §4/§5).
 *
 * Ein Recht wirkt nur, wenn der Code es prüft — deshalb ist der Katalog fest
 * und steht zeichengleich in der jüngsten Fassung der Migrationen: als CHECK
 * in Nr. 188 (`supabase/talentpool.sql`), als `rechte_katalog()` in Nr. 189
 * (`supabase/talentpool_rpc.sql`). Startrollen und Standard-Matrix stehen
 * weiter in Nr. 179 (`supabase/material_rpc.sql`) — der Talentpool bringt
 * keine Startrolle mit. Nr. 178/179 tragen den Stand mit 23 Rechten,
 * Nr. 149/150 (`rollen_rechte*.sql`) den älteren mit 21; alle bleiben
 * unverändert (auf PROD mit Prüfsumme gebucht).
 * `rechteKatalogSql.test.ts` hält Code und SQL gegeneinander. Die Matrix im
 * Admin schaltet nur, welche Rolle welches Recht hat.
 *
 * `admin` und `super_admin` haben immer alle Rechte und stehen nicht in der
 * Matrix; Freelancer haben keine. Reine Daten und Funktionen, kein Supabase.
 */

export type RechtGruppe =
    | 'projekte' | 'finanzen' | 'rechnungen' | 'vertrieb' | 'disposition' | 'sifa' | 'dokumente' | 'material'
    | 'personal';

export type RechtSchluessel =
    | 'projekte.alle_sehen'
    | 'projekte.sigeko_anlegen'
    | 'projekte.sifa_anlegen'
    | 'projekte.hse_anlegen'
    | 'projekte.zuweisen'
    | 'projekte.archivieren'
    | 'preise.sehen'
    | 'leistung.fuer_andere_buchen'
    | 'rechnungen.lesen'
    | 'rechnungen.bearbeiten'
    | 'rechnungen.ausstellen'
    | 'zahlungen.buchen'
    | 'datev.export'
    | 'kunden.verwalten'
    | 'leads.verwalten'
    | 'angebote.verwalten'
    | 'erp.steuerung'
    | 'freelancer.verwalten'
    | 'urlaub.fuer_andere'
    | 'sifa.vorlagen'
    | 'dokumente.nur_admin'
    | 'material.beschaffen'
    | 'material.verwalten'
    | 'talentpool.verwalten';

export interface RechtInfo {
    schluessel: RechtSchluessel;
    gruppe: RechtGruppe;
    /** Kurz, für die Matrixzeile. */
    titel: string;
    /** Ein Satz: was das Recht freigibt. Steht als Hinweis an der Zeile. */
    beschreibung: string;
}

export const RECHT_GRUPPEN: readonly { schluessel: RechtGruppe; titel: string }[] = [
    { schluessel: 'projekte', titel: 'Projekte' },
    { schluessel: 'finanzen', titel: 'Finanzen' },
    { schluessel: 'rechnungen', titel: 'Rechnungen' },
    { schluessel: 'vertrieb', titel: 'Vertrieb' },
    { schluessel: 'disposition', titel: 'Disposition' },
    { schluessel: 'sifa', titel: 'SiFa' },
    { schluessel: 'dokumente', titel: 'Dokumente' },
    { schluessel: 'material', titel: 'Material' },
    { schluessel: 'personal', titel: 'Personal' },
];

export const RECHTE: readonly RechtInfo[] = [
    { schluessel: 'projekte.alle_sehen', gruppe: 'projekte', titel: 'Alle Projekte sehen',
      beschreibung: 'Schalter „Alle“ in Projektliste und Karte; fremde Projekte öffnen ohne Begründung.' },
    { schluessel: 'projekte.sigeko_anlegen', gruppe: 'projekte', titel: 'SiGeKo-Projekt anlegen',
      beschreibung: 'Projekt-Assistent, Import und Angebotsannahme für den Typ SiGeKo.' },
    { schluessel: 'projekte.sifa_anlegen', gruppe: 'projekte', titel: 'SiFa-Betreuung anlegen',
      beschreibung: 'Projekt-Assistent, Import und Angebotsannahme für den Typ SiFa.' },
    { schluessel: 'projekte.hse_anlegen', gruppe: 'projekte', titel: 'HSE-Projekt anlegen',
      beschreibung: 'Projekt-Assistent, Import und Angebotsannahme für den Typ HSE.' },
    { schluessel: 'projekte.zuweisen', gruppe: 'projekte', titel: 'Verantwortliche zuweisen',
      beschreibung: 'Hauptverantwortlichen, Vertreter und Planung ändern; Projekt übertragen.' },
    { schluessel: 'projekte.archivieren', gruppe: 'projekte', titel: 'Projekt archivieren',
      beschreibung: 'Projekt beenden und archivieren.' },
    { schluessel: 'preise.sehen', gruppe: 'finanzen', titel: 'Preise sehen',
      beschreibung: 'Preise, Summen und Budget in Projekt und Modulen.' },
    { schluessel: 'leistung.fuer_andere_buchen', gruppe: 'finanzen', titel: 'Leistung für andere buchen',
      beschreibung: 'Feld „Erbracht durch“ beim Nachbuchen einer Leistung.' },
    { schluessel: 'rechnungen.lesen', gruppe: 'rechnungen', titel: 'Rechnungen lesen',
      beschreibung: 'Rechnungsliste, Buchhaltungsansicht und Nachweise.' },
    { schluessel: 'rechnungen.bearbeiten', gruppe: 'rechnungen', titel: 'Rechnungen bearbeiten',
      beschreibung: 'Entwürfe anlegen und ändern, Rechnungslauf.' },
    { schluessel: 'rechnungen.ausstellen', gruppe: 'rechnungen', titel: 'Rechnungen ausstellen',
      beschreibung: 'Ausstellen, stornieren, Gutschrift.' },
    { schluessel: 'zahlungen.buchen', gruppe: 'rechnungen', titel: 'Zahlungen buchen',
      beschreibung: 'Bankimport, Zahlungen, Skonto, Einbehalt.' },
    { schluessel: 'datev.export', gruppe: 'rechnungen', titel: 'DATEV-Export',
      beschreibung: 'Buchungsstapel für die Steuerkanzlei exportieren.' },
    { schluessel: 'kunden.verwalten', gruppe: 'vertrieb', titel: 'Kunden verwalten',
      beschreibung: 'Kunden anlegen und ändern.' },
    { schluessel: 'leads.verwalten', gruppe: 'vertrieb', titel: 'Neukunden / Leads',
      beschreibung: 'Neukunden, Lead-Scout und Vertriebskarte.' },
    { schluessel: 'angebote.verwalten', gruppe: 'vertrieb', titel: 'Angebote',
      beschreibung: 'Angebote schreiben und annehmen (Annahme legt das Projekt an).' },
    { schluessel: 'erp.steuerung', gruppe: 'disposition', titel: 'ERP / Cockpit',
      beschreibung: 'ERP-Steuerung (Auftragsbestand, Nachkalkulation, Liquidität).' },
    { schluessel: 'freelancer.verwalten', gruppe: 'disposition', titel: 'Freelancer verwalten',
      beschreibung: 'Freelancer-Stamm, Aufträge und Gutschriften.' },
    { schluessel: 'urlaub.fuer_andere', gruppe: 'disposition', titel: 'Urlaub für andere planen',
      beschreibung: 'Im Urlaubsplaner „Planen für“ einen Kollegen wählen.' },
    { schluessel: 'sifa.vorlagen', gruppe: 'sifa', titel: 'SiFa-Vorlagen',
      beschreibung: 'Unterweisungs-Vorlagen anlegen und ändern.' },
    { schluessel: 'dokumente.nur_admin', gruppe: 'dokumente', titel: '„Nur Admin“-Dokumente',
      beschreibung: 'Dokumente sehen, die als „nur Admin“ markiert sind.' },
    // Materialverwaltung (docs/materialverwaltung-plan.md, Nr. 178/179).
    { schluessel: 'material.beschaffen', gruppe: 'material', titel: 'Materialmeldungen bearbeiten',
      beschreibung: 'Bekommt jede Materialmeldung per Glocke und E-Mail und setzt „Bestellt“ und „Aufgefüllt“.' },
    { schluessel: 'material.verwalten', gruppe: 'material', titel: 'Material verwalten',
      beschreibung: 'Artikel anlegen und ändern, Nachbestellkarten drucken und erneuern.' },
    // Talentpool (docs/talentpool-plan.md, Nr. 188/189): Bewerbungen von der Website.
    { schluessel: 'talentpool.verwalten', gruppe: 'personal', titel: 'Talentpool (Bewerbungen)',
      beschreibung: 'Bewerbungen von der Website sehen, bearbeiten und löschen. Enthält Personendaten.' },
];

export const ALLE_RECHTE: readonly RechtSchluessel[] = RECHTE.map(r => r.schluessel);

const RECHT_MENGE: ReadonlySet<string> = new Set(ALLE_RECHTE);

export const istRecht = (x: unknown): x is RechtSchluessel =>
    typeof x === 'string' && RECHT_MENGE.has(x);

export type StartRolle =
    | 'sigeko' | 'sifa' | 'hse' | 'arbeitsvorbereitung' | 'vertrieb' | 'disposition' | 'buchhaltung'
    | 'beschaffung';

export interface RolleInfo {
    schluessel: string;
    name: string;
    beschreibung: string;
    reihenfolge: number;
    /** Startrolle: nicht löschbar. Eigene Rollen eines Admins: false. */
    system: boolean;
}

export const STARTROLLEN: readonly (RolleInfo & { schluessel: StartRolle })[] = [
    { schluessel: 'sigeko', name: 'SiGeKo', beschreibung: 'Sicherheits- und Gesundheitsschutzkoordination auf der Baustelle.', reihenfolge: 10, system: true },
    { schluessel: 'sifa', name: 'SiFa', beschreibung: 'Fachkraft für Arbeitssicherheit, Betreuung und Unterweisungen.', reihenfolge: 20, system: true },
    { schluessel: 'hse', name: 'HSE', beschreibung: 'HSSE-Management in internationalen Projekten.', reihenfolge: 30, system: true },
    { schluessel: 'arbeitsvorbereitung', name: 'Arbeitsvorbereitung', beschreibung: 'Plant über den gesamten Projektbestand.', reihenfolge: 40, system: true },
    { schluessel: 'vertrieb', name: 'Vertrieb', beschreibung: 'Kunden, Neukunden und Angebote.', reihenfolge: 50, system: true },
    { schluessel: 'disposition', name: 'Disposition', beschreibung: 'Verantwortliche zuweisen, Urlaub, Freelancer, ERP.', reihenfolge: 60, system: true },
    { schluessel: 'buchhaltung', name: 'Buchhaltung', beschreibung: 'Rechnungen, Zahlungen, DATEV.', reihenfolge: 70, system: true },
    // Zusatzrolle aus Nr. 179: wer sie trägt, bekommt die Materialmeldungen.
    { schluessel: 'beschaffung', name: 'Beschaffung', beschreibung: 'Materialmeldungen empfangen, bestellen und auffüllen.', reihenfolge: 80, system: true },
];

/** Standard-Matrix §5 — Startwerte je Firma; danach pflegt der Admin. */
export const STANDARD_MATRIX: Readonly<Record<StartRolle, readonly RechtSchluessel[]>> = {
    sigeko: ['projekte.sigeko_anlegen'],
    sifa: ['projekte.sifa_anlegen', 'sifa.vorlagen'],
    hse: ['projekte.hse_anlegen'],
    arbeitsvorbereitung: ['projekte.alle_sehen', 'projekte.sigeko_anlegen', 'projekte.sifa_anlegen', 'projekte.hse_anlegen'],
    vertrieb: [
        'projekte.alle_sehen', 'projekte.sigeko_anlegen', 'projekte.sifa_anlegen', 'projekte.hse_anlegen',
        'preise.sehen', 'rechnungen.lesen', 'kunden.verwalten', 'leads.verwalten', 'angebote.verwalten',
    ],
    disposition: [
        'projekte.alle_sehen', 'projekte.sigeko_anlegen', 'projekte.sifa_anlegen', 'projekte.hse_anlegen',
        'projekte.zuweisen', 'projekte.archivieren', 'preise.sehen',
        'erp.steuerung', 'freelancer.verwalten', 'urlaub.fuer_andere',
    ],
    buchhaltung: [
        'projekte.alle_sehen', 'projekte.archivieren', 'preise.sehen', 'leistung.fuer_andere_buchen',
        'rechnungen.lesen', 'rechnungen.bearbeiten', 'rechnungen.ausstellen', 'zahlungen.buchen', 'datev.export',
        'kunden.verwalten', 'erp.steuerung', 'freelancer.verwalten',
    ],
    // `material.verwalten` hat ab Werk keine Rolle: Artikel und Karten bleiben
    // bei den Admins, bis einer das Häkchen in der Matrix setzt.
    // Ebenso `talentpool.verwalten` (Nr. 188/189): Bewerbungen sind
    // Personendaten — ab Werk sehen sie nur Admins.
    beschaffung: ['material.beschaffen'],
};

/**
 * Rechte, solange die Datenbank das Rollensystem noch nicht kennt (fail-open):
 * bildet das HEUTIGE Verhalten der Systemrolle ab, damit ein Client vor den
 * Migrationen Nr. 149/150 nichts verliert und nichts dazugewinnt.
 *   admin / super_admin → alles
 *   arbeitsvorbereitung → alle Projekte sehen + alle drei Projekttypen anlegen
 *   user                → alle drei Projekttypen anlegen
 *   freelancer / unbekannt → nichts
 * Die Material-Rechte (Nr. 178/179) haben im Rückfall nur Admins: ohne
 * Migration gibt es keine Rolle „Beschaffung“ und keine Tabellen dahinter.
 * Dasselbe gilt für den Talentpool (Nr. 188/189).
 */
export const rechteAusSystemrolle = (role: string | null | undefined): ReadonlySet<RechtSchluessel> => {
    if (role === 'admin' || role === 'super_admin') return new Set(ALLE_RECHTE);
    const anlegen: RechtSchluessel[] = ['projekte.sigeko_anlegen', 'projekte.sifa_anlegen', 'projekte.hse_anlegen'];
    if (role === 'arbeitsvorbereitung') return new Set<RechtSchluessel>(['projekte.alle_sehen', ...anlegen]);
    if (role === 'user') return new Set<RechtSchluessel>(anlegen);
    return new Set();
};
